/* eslint-disable react/no-unused-state */
import React from 'react';
import moment from 'moment';

import { HTTP_METHOD } from '@corporate-initiatives/ci-portal-js-sdk';
import {
  IUploadMaintenanceChecksModalWizardState,
  IUploadMaintenanceChecksModalWizardPage1Data,
  uploadMaintenanceChecksModalWizardInitialState,
  IUploadValidationResponse,
} from './upload-maintenance-checks-data.interface';
import { IModalWizardPageDataError } from '../../../types/modal/modal-wizard-page-data-error.interface';
import { IModalWizardPageData } from '../../../types/modal/modal-wizard-page-data.interface';
import { IContractRecord } from '../../../types/service/contract.record.interface';
import { ConfirmActionModalResult } from '../../../types/modal/modal-result';
import { ModalProps } from '../../../types/modal/modal.props';

import { APIContextProps, connectToAPIProvider } from '../../providers/api-provider';

import { ModalWizard } from '../modal-wizard';
import { UploadMaintenanceChecksModalPage1 } from './wizard-pages/upload-maintenance-checks-modal-page-1';
import { UploadMaintenanceChecksModalPage2 } from './wizard-pages/upload-maintenance-checks-modal-page-2';
import { UploadMaintenanceChecksModalPage3 } from './wizard-pages/upload-maintenance-checks-modal-page-3';

import { fileCompare } from '../../../helpers/file-compare.helper';
import { SERObject } from '../../../helpers/ser-object.helper';
import { apiAborter } from '../../../helpers/api-aborter.helper';

import { THEME_COLOR } from '../../../constants/theme-color.const';
import { MODAL_WIZARD_BUTTON_TYPE, A_MODAL_WIZARD_BUTTON_TYPE } from '../../../constants/modal-wizard-button-type.const';
import { UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE, AN_UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE } from './upload-maintenance-checks-modal-wizard-page.const';
import { DATE_FORMAT } from '../../../constants/date-format.const';
import { uploadFileDirect } from '../../../helpers/file-upload.helper';

export type UploadMaintenanceChecksModalWizardProps = ModalProps<ConfirmActionModalResult> & {
  apiProvider: APIContextProps,
  initialData: IContractRecord,
  onModalComplete: (success: boolean) => unknown,
}

class UploadMaintenanceChecksModalWizardComponent extends React.Component<UploadMaintenanceChecksModalWizardProps, IUploadMaintenanceChecksModalWizardState> {
  private loadProjectOrderAbortController: null | AbortController;

  private findProjectInvoicingContactAbortController: null | AbortController;

  private findProjectBillingAgentCompanyAbortController: null | AbortController;

  private uploadMaintenanceChecksAbortController: null | AbortController;

  /**
   * @constructor
   */
  constructor(props: UploadMaintenanceChecksModalWizardProps) {
    super(props);
    this.state = uploadMaintenanceChecksModalWizardInitialState;

    this.loadProjectOrderAbortController = null;
    this.findProjectInvoicingContactAbortController = null;
    this.findProjectBillingAgentCompanyAbortController = null;
    this.uploadMaintenanceChecksAbortController = null;
  }


  /**
   * @inheritdoc
   */
  componentDidMount = () => {
    this.initialise();
  }


  /**
   * @inheritdoc
   */
  componentWillUnmount = () => {
    if (this.loadProjectOrderAbortController) this.loadProjectOrderAbortController.abort();
    if (this.findProjectInvoicingContactAbortController) this.findProjectInvoicingContactAbortController.abort();
    if (this.findProjectBillingAgentCompanyAbortController) this.findProjectBillingAgentCompanyAbortController.abort();
    if (this.uploadMaintenanceChecksAbortController) this.uploadMaintenanceChecksAbortController.abort();
  }


  /**
   * When the wizard is loaded, we have to fetch some pre-existing information about the contract
   */
  private initialise = async () => {
    // The initial data is a contract record. Just use that
    const { initialData: contractRecord } = this.props;
    const { pageData } = this.state;

    this.setState({
      contractRecord,
    }, async () => {
      this.setState({
        // activatingProjectOrderRecord,

        // Populate some of the page data from the contract record
        pageData: {
          ...pageData,

          // Page 1
          [UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_1_CHOOSE_FILE]: {
            ...pageData[UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_1_CHOOSE_FILE],
          },
        },
      }, async () => {
        // TODO: more initialisation
        // Initialisation complete
        this.setState({
          initialising: false,
        });
      });
    });
  }


  private ingestFileForValidation = async () => {
    // Reset the state of the upload data
    this.setState({
      processingImport: true,
      importProcessed: false,
      uploadImportFileResult: null,
    }, async () => {
      const importResult = await this.uploadImportSheet();
      if (importResult.success) {
        this.setState({
          importFile: null,
          processingImport: false,
          importProcessed: true,
          importFileUploadStatus: 100,
          uploadImportFileResult: importResult,
        });
      } else {
        this.setState({
          processingImport: false,
          importProcessed: false,
          importFileUploadStatus: 0,
          uploadImportFileResult: null,
        });
      }
    });
  }


  /**
   * Performs all of the actions which actually represent the contract activation
   */
  private processProjectActivation = async () => {
    // Reset the state of the activation data
    this.setState({
      processing: true,
      success: false,
    }, async () => {
      // Record the Result
      this.setState({
        processing: true,
      }, async () => {
        // Execute the Upload Maintenance Checks action
        const importMaintenanceChecksResult = await this.executeUploadMaintenanceChecksAction();
        const { contractRecord } = this.state;

        // Record the result
        this.setState({
          importMaintenanceChecksResult,
          contractRecord: importMaintenanceChecksResult.success ? importMaintenanceChecksResult.result : contractRecord,
        }, async () => {
          // If the contract activation was successful, the modal can be closed.
          if (importMaintenanceChecksResult.success) {
            // Processing Complete
            this.setState({
              processing: false,
              success: true,
              isDirty: false,
            });
          } else {
            // Update Project failed
            this.setState({
              processing: false,
              success: false,
            });
          }
        });
      });
    });
  }


  /**
   * Fired by the wizard when the user attempts to progress forward.
   *
   * Returns true if the page validated successfully otherwise it returns an array of data errors
   */
  private handleValidatePageData = (pageIndex: number, pageData: IModalWizardPageData): true | IModalWizardPageDataError[] => {
    const dataErrors: IModalWizardPageDataError[] = [];

    switch (pageIndex as AN_UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE) {
      // Page 1 - Activating Purchase Order Information
      case UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_1_CHOOSE_FILE: {
        const typedPageData = pageData as IUploadMaintenanceChecksModalWizardPage1Data;
        if (!typedPageData.schedule_import_file) {
          dataErrors.push({ fieldName: 'schedule_import_file', error: 'You must upload an import file.' });
        }
        break;
      }
    }
    return (dataErrors.length > 0) ? dataErrors : true;
  };


  /**
   * Fired by the wizard when the user changes any of the page data and just before the state is updated.
   * This gives us a chance to hook into any of the data changes and make additional changes prior to being committed to the state.
   *
   * Return PageData with alterations to augment / change / hook the change of page data
   * Return null to leave the pageData unchanged.
   */
  private handleBeforePageDataChanged = (pageIndex: number, pageData: IModalWizardPageData): null | IModalWizardPageData => {
    switch (pageIndex as AN_UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE) {
      // Page 1
      case UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_1_CHOOSE_FILE: {
        const { pageData: oldPageData } = this.state;
        const typedPageData = pageData as IUploadMaintenanceChecksModalWizardPage1Data;
        const oldPage1Data = oldPageData[UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_1_CHOOSE_FILE];

        // The activation is an email_only_activation and the user changed the email_only_activation or the received date
        // Update the auto-generated order number
        if (
          (typedPageData.email_only_activation && (
            (typedPageData.email_only_activation !== oldPage1Data.email_only_activation) ||
            (typedPageData.received_date !== oldPage1Data.received_date)
          ))
        ) {
          typedPageData.order_no = `Email ${
            typedPageData.received_date
              ? moment(typedPageData.received_date, DATE_FORMAT.YEAR_MONTH_DAY_DASHES, true).format(DATE_FORMAT.DAY_MONTH_YEAR_SLASHES)
              : ''
          }`;
        }

        // Has the user selected a new Activating PO Document File?
        if (!fileCompare(typedPageData.schedule_import_file, oldPage1Data.schedule_import_file)) {
          // Reset the activating PO Document (outside of this state update)
          setImmediate(() => this.setState({
            importFile: null,
            importFileUploadStatus: 0,
          }));
        }

        return typedPageData;
      }
    }

    return null;
  };


  /**
   * Ensure the right buttons are available at the right time
   */
  private handleCheckButtonVisible = (buttonType: A_MODAL_WIZARD_BUTTON_TYPE, pageIndex: number, defaultButtonVisible: boolean) => {
    const { processing, success } = this.state;

    if (buttonType === MODAL_WIZARD_BUTTON_TYPE.CANCEL) {
      // Don't show the cancel button on the last page unless processing failed
      return (
        (pageIndex < UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_3_PROCESSING)
        ||
        (
          (pageIndex === UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_2_VERIFICATION) &&
          (processing === false) &&
          (success === false)
        )
      );
    }

    if (buttonType === MODAL_WIZARD_BUTTON_TYPE.SUBMIT) {
      // Only show the submit button on the second last page prior to processing
      return pageIndex === UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_2_VERIFICATION;
    }

    if (buttonType === MODAL_WIZARD_BUTTON_TYPE.NEXT) {
      // Don't show the next button on the second last or last pages
      return pageIndex < UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_2_VERIFICATION;
    }

    if (buttonType === MODAL_WIZARD_BUTTON_TYPE.PREV) {
      // Don't show the previous button on the last page unless processing failed
      return (
        (
          (pageIndex > UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_1_CHOOSE_FILE) &&
          (pageIndex < UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_3_PROCESSING)
        )
        ||
        (
          (pageIndex === UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_3_PROCESSING) &&
          (processing === false) &&
          (success === false)
        )
      );
    }

    // use the default visibility for all other situations / button types
    return defaultButtonVisible;
  };


  /**
   * Ensure buttons are enabled at the appropriate time
   */
  private handleCheckButtonEnabled = (buttonType: A_MODAL_WIZARD_BUTTON_TYPE, pageIndex: number, defaultButtonEnabled: boolean) => {
    const { initialising, pageData } = this.state;

    // Disable all but the cancel button while initialising
    if (initialising && (buttonType !== MODAL_WIZARD_BUTTON_TYPE.CANCEL)) return false;

    // Disable the "next" button on Page 1 if no file is selected
    const {
      schedule_import_file,
    } = pageData[UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_1_CHOOSE_FILE];

    if (
      (pageIndex === UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_1_CHOOSE_FILE) &&
      (buttonType === MODAL_WIZARD_BUTTON_TYPE.NEXT) &&
      (!schedule_import_file)
    ) {
      return false;
    }

    return defaultButtonEnabled;
  }


  /**
   * Handle the click of one of the wizard buttons
   */
  private handleButtonClick = (buttonType: A_MODAL_WIZARD_BUTTON_TYPE, pageIndex: number): boolean => {
    // If the next button was clicked on P1, begin processing the activation
    if ((buttonType === MODAL_WIZARD_BUTTON_TYPE.NEXT) && (pageIndex === UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_1_CHOOSE_FILE)) {
      // move to the last page
      this.setState(
        {
          currentPageIndex: UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_2_VERIFICATION,
        },
        // kick off the upload
        this.ingestFileForValidation,
      );

      // Let the wizard know that the button click was handled
      return true;
    }

    // If the submit button was clicked, begin processing the activation
    if ((buttonType === MODAL_WIZARD_BUTTON_TYPE.SUBMIT) && (pageIndex === UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_2_VERIFICATION)) {
      // move to the last page
      this.setState({
        currentPageIndex: UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_3_PROCESSING,
      },
      // kick off the processing
      this.processProjectActivation);

      // Let the wizard know that the button click was handled
      return true;
    }

    // Returning false indicates that the button click was not handled.
    return false;
  };


  /**
   * Call the Upload Maintenance Checks action
   */
  private executeUploadMaintenanceChecksAction = async () => {
    const importMaintenanceChecksResult = new SERObject<IContractRecord>(false);

    const {
      uploadImportFileResult,
    } = this.state;

    const {
      apiProvider: { apiFetch },
      initialData: { id: contract_id },
    } = this.props;

    let abortController = this.uploadMaintenanceChecksAbortController;
    if (abortController) {
      abortController.abort();
    }
    abortController = apiAborter();
    this.uploadMaintenanceChecksAbortController = abortController;

    const response = await apiFetch(
      `/contract/${contract_id}/action/upload-maintenance-checks`,
      {
        name: 'UploadMaintenanceChecksModalWizard::executeUploadMaintenanceChecksAction',
        method: HTTP_METHOD.POST,
        signal: abortController.signal,
        body: {
          step: 'import',
          records: uploadImportFileResult?.result || [],
        },
      },
    );

    if (response.success) {
      importMaintenanceChecksResult.result = response.body.data;
      importMaintenanceChecksResult.success = true;
    } else {
      console.error('Failed to create Project Order: ', response.error, response.body);
      importMaintenanceChecksResult.error = (response.body && response.body.errors) ? response.body.errors : response.error;
    }

    return importMaintenanceChecksResult;
  }


  /**
   * Upload the specified import sheet
   */
  private uploadImportSheet = async (): Promise<SERObject<IUploadValidationResponse[]>> => {
    const {
      pageData,
    } = this.state;

    const {
      initialData: {
        id: contract_id,
      },
    } = this.props;

    // Get data from Page 1
    const {
      schedule_import_file,
    } = pageData[UPLOAD_MAINTENANCE_CHECKS_MODAL_WIZARD_PAGE.PAGE_1_CHOOSE_FILE];

    // Bail if the Activating PO Document has not been selected
    if (!schedule_import_file) {
      const result = new SERObject<IUploadValidationResponse[]>(false);
      // Nothing to Upload, return success.
      result.success = true;
      result.result = null;
      return result;
    }

    // Prepare a callback for updating the upload progress
    const onUploadProgress = (progress: number) => {
      this.setState({
        importFileUploadStatus: progress,
      });
    };

    // Request the Upload
    const formData = new FormData();
    formData.append('schedule_import_file', schedule_import_file);
    formData.append('step', 'validate');

    // Upload the File
    const uploadResult = await uploadFileDirect<IUploadValidationResponse[]>(
      formData,
      {
        url: `/contract/${contract_id}/action/upload-maintenance-checks`,
        headers: {
          Accept: 'application/json',
        },
        method: HTTP_METHOD.POST,
        filename: 'upload.xlsx',
        expires: 1000,
      },
      onUploadProgress,
    );
    if (uploadResult.success) {
      this.setState({
        uploadImportFileResult: uploadResult,
        processingImport: false,
        importProcessed: true,
      });
    } else {
      this.setState({
        uploadImportFileResult: null,
        processingImport: false,
        importProcessed: false,
      });
    }

    return uploadResult;
  }


  /**
   * @inheritdoc
   */
  public render = (): JSX.Element => {
    const {
      initialData: {
        number,
      },
      id: modal_id,
      closeModal,
      onModalComplete,
    } = this.props;

    const {
      isDirty,
      currentPageIndex,
    } = this.state;

    return (
      <ModalWizard<IUploadMaintenanceChecksModalWizardState, ConfirmActionModalResult>
        {...this.props}
        color={THEME_COLOR.PRIMARY}
        size="lg"
        title={`Upload Maintenance Checks for ${number}`}
        allowDismiss={!isDirty && currentPageIndex === 0}
        isDirty={isDirty || currentPageIndex > 0}
        pageComponents={[
          UploadMaintenanceChecksModalPage1,
          UploadMaintenanceChecksModalPage2,
          UploadMaintenanceChecksModalPage3,
        ]}
        wizardState={this.state}
        setWizardState={(newState: unknown, callback?: () => void) => {
          this.setState(newState as IUploadMaintenanceChecksModalWizardState, callback);
        }}
        onValidatePageData={this.handleValidatePageData}
        onPageIndexChanged={() => this.setState({ isDirty: true })}
        onPageDataChanged={() => this.setState({ isDirty: true })}
        onBeforePageDataChanged={this.handleBeforePageDataChanged}
        onModalCancel={() => closeModal(modal_id, isDirty)}
        onModalComplete={() => onModalComplete(true)}
        onCheckButtonVisible={this.handleCheckButtonVisible}
        onCheckButtonEnabled={this.handleCheckButtonEnabled}
        onButtonClick={this.handleButtonClick}
      />
    );
  }
}


// Wrap the Upload Maintenance Checks Modal Wizard in an API Provider
export const UploadMaintenanceChecksModalWizard = connectToAPIProvider(UploadMaintenanceChecksModalWizardComponent);
